import { createAsyncThunk } from '@reduxjs/toolkit';
import { sendLegalQuery } from '../../common/backend-api/backend-api';
import { DEFAULT_JURISDICTION, JurisdictionCode } from '../../common/jurisdictions';
import { getLanguageByCode } from '../../i18n/languages';
import { AILegalMessage } from '../../models/ai-legal-message';
import {
  completeProgress as completeOldProgress
} from '../slices/legalQueryProgressSlice';
import {
  completeProgress,
  startProgress,
  updateProgress
} from '../slices/simpleLegalProgressSlice'; // ✅ Import new simple progress
import { RootState } from '../store';
import {
  caseSelectOneObjectByQuery,
  DBMessageThunks,
  knowledgebaseSelectOneObjectByQuery,
  userSelectOneObjectByQuery
} from './DBServiceThunks';
import { createKnowledgebaseForCase } from './KnowledgebaseUtils';

// Thunk to handle submitting a legal query
export const handleSubmitQueryThunk = createAsyncThunk(
  'aiLegalMessage/handleSubmitQuery',
  async (
    { chatId, query, caseId, owner, jurisdiction }: { chatId: string, query: string, caseId: string, owner: string, jurisdiction?: JurisdictionCode },
    { dispatch, getState }
  ) => {
    // Generate a unique query ID for progress tracking
    const finalQueryId = `query_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;

    try {
      console.log(`🚀 [PROGRESS_DEBUG] handleSubmitQueryThunk called with queryId: ${finalQueryId}`);
      console.log(`🚀 [PROGRESS_DEBUG] About to dispatch startProgress action...`);

      // ✅ Initialize simple progress tracking - backend will handle calculations
      dispatch(startProgress({ queryId: finalQueryId, chatId, caseId }));

      console.log(`✅ [PROGRESS_DEBUG] startProgress dispatched successfully for queryId: ${finalQueryId}`);


      const state = getState() as RootState;
      const currentChatId = chatId;

      // Get current user to extract language preference
      const currentUser = userSelectOneObjectByQuery(state.DBUserReducer, {});

      // Convert user's language preference from ISO code to full language name
      const userLanguageCode = currentUser?.language || 'en'; // Default to English if no preference
      const userLanguage = getLanguageByCode(userLanguageCode);
      const languageCode = userLanguage.code; // e.g., "en", "de", "fr"

      console.log(`[AILegalMessageThunk] User language: ${userLanguageCode} -> ${languageCode}`);

      // Retrieve the knowledge base for the given case and owner
      const knowledgeBase = knowledgebaseSelectOneObjectByQuery(
        state.DBKnowledgebaseReducer,
        {
          caseId: caseId,
          ownerId: owner
        });

      // Ensure knowledge base exists - create one if missing (failsafe mechanism)
      let finalKnowledgeBase = knowledgeBase;
      if (!knowledgeBase || !knowledgeBase._id) {
        console.warn(`[AILegalMessageThunk] No knowledge base found for case ID ${caseId} and owner ${owner}. Creating one as failsafe...`);

        try {
          // Get the case details to extract name and jurisdiction
          const caseDetails = caseSelectOneObjectByQuery(state.DBCaseReducer, { _id: caseId });

          if (!caseDetails) {
            throw new Error(`Case not found with ID ${caseId}`);
          }

          const createdKnowledgeBase = await createKnowledgebaseForCase(
            dispatch,
            caseId,
            owner,
            caseDetails.title,
            caseDetails.jurisdiction,
            {
              throwOnError: true,
              logPrefix: '[AILegalMessageThunk - Failsafe]'
            }
          );

          if (createdKnowledgeBase) {
            finalKnowledgeBase = createdKnowledgeBase;
          } else {
            throw new Error('Knowledge base creation returned null');
          }

        } catch (kbCreateError) {
          const errorMessage = `Failsafe knowledge base creation failed for case ID ${caseId} and owner ${owner}: ${kbCreateError instanceof Error ? kbCreateError.message : 'Unknown error'}`;
          console.error(`[AILegalMessageThunk] ${errorMessage}`);
          dispatch(completeOldProgress({ queryId: finalQueryId, success: false, details: errorMessage }));
          throw new Error(errorMessage);
        }
      }

      // Create user message
      console.log('[DEBUG] caseId before message creation:', {
        value: caseId,
        type: typeof caseId,
        constructor: caseId?.constructor?.name,
        isString: typeof caseId === 'string'
      });

      const userMessage = {
        ownerId: owner,
        caseId: caseId,
        chatId: currentChatId,
        sender: 'user' as 'assistant' | 'user',
        content: query,
        timestamp: new Date().toISOString(),
        version: 1
      };

      console.log('[DEBUG] userMessage object:', JSON.stringify(userMessage, null, 2));

      await dispatch(DBMessageThunks.create(userMessage));

      console.log(`[AILegalMessageThunk] Sending query to backend with queryId: ${finalQueryId}`);

      // Call the sendLegalQuery endpoint - backend will handle ALL progress updates via MQTT
      // ✅ Non-blocking submission - fire and forget
      await dispatch(
        sendLegalQuery.initiate({
          queryText: query,
          caseId: caseId,
          chatId: currentChatId,
          knowledgebaseId: finalKnowledgeBase._id,
          queryId: finalQueryId, // Pass the queryId to backend for progress tracking
          jurisdiction: jurisdiction || DEFAULT_JURISDICTION,
          language: languageCode // Pass the language code (e.g., "en", "de")
        })
      );

      console.log(`[AILegalMessageThunk] Query submitted for processing (non-blocking) for queryId: ${finalQueryId}`);

      // ✅ Poll for assistant message creation with retries
      // Backend might take a few seconds to create the message with status: 'active'
      const checkForAssistantMessage = async (retries = 0, maxRetries = 10) => {
        if (retries >= maxRetries) {
          console.warn(`[AILegalMessageThunk] ❌ Max retries (${maxRetries}) reached, giving up on detecting assistant message`);
          return;
        }

        console.log(`[AILegalMessageThunk] 🔍 Checking for assistant message (attempt ${retries + 1}/${maxRetries}) after ${retries * 3}s`);

        try {
          const result = await dispatch(DBMessageThunks.find({
            chatId: currentChatId,
            sender: 'assistant',
            'metadata.queryId': finalQueryId,
            $paginate: false
          })).unwrap();

          const messages = Array.isArray(result) ? result : result.data || [];
          const assistantMessage = messages.find((msg: any) => msg.metadata?.queryId === finalQueryId);

          if (assistantMessage) {
            console.log(`✅ [AILegalMessageThunk] Found assistant message with queryId: ${finalQueryId}, chatId: ${assistantMessage.chatId}`);

            // Fetch all messages to update Redux and trigger polling
            await dispatch(DBMessageThunks.find({
              chatId: currentChatId,
              $paginate: false
            })).unwrap();

            console.log(`✅ [AILegalMessageThunk] Messages fetched, polling should start now!`);
          } else {
            console.log(`⏳ [AILegalMessageThunk] Assistant message not found yet, retrying in 3s...`);
            setTimeout(() => checkForAssistantMessage(retries + 1, maxRetries), 3000);
          }
        } catch (error) {
          console.error(`❌ [AILegalMessageThunk] Error checking for assistant message:`, error);
          // Retry on error
          setTimeout(() => checkForAssistantMessage(retries + 1, maxRetries), 3000);
        }
      };

      // Start checking after 3 seconds
      setTimeout(() => checkForAssistantMessage(), 3000);

      // ✅ Return immediately - polling will handle completion detection
      return { queryId: finalQueryId };

    } catch (error) {
      console.error(`[AILegalMessageThunk] Error in handleSubmitQueryThunk for queryId: ${finalQueryId}:`, error);

      // ✅ Complete progress with error (old system for compatibility)
      dispatch(completeOldProgress({
        queryId: finalQueryId,
        success: false,
        details: error instanceof Error ? error.message : 'Unknown error occurred'
      }));

      // Re-throw to allow the calling component to handle the error
      throw error;
    }
  }
);

// ✅ Polling thunk to check message status
export const pollMessageStatusThunk = createAsyncThunk(
  'aiLegalMessage/pollMessageStatus',
  async (
    { chatId, queryId }: { chatId: string; queryId?: string },
    { dispatch }
  ) => {
    try {
      // ✅ Use queryId for precise message matching when available
      const queryParams: any = {
        chatId,
        sender: 'assistant',
        $sort: { timestamp: -1 },
        $limit: 1,
        $paginate: false
      };

      // If queryId provided, find the SPECIFIC assistant message for this query
      if (queryId) {
        queryParams['metadata.queryId'] = queryId;
      }

      const result = await dispatch(DBMessageThunks.find(queryParams)).unwrap();

      const messages = result as AILegalMessage[];
      const latestMessage = messages[0];

      if (!latestMessage) {
        return { shouldContinue: true, reason: 'no_assistant_message_yet' };
      }

      // Check if message is completed or has error
      const status = latestMessage.metadata?.status;
      const messageQueryId = latestMessage.metadata?.queryId;

      if (status === 'completed' || status === 'error') {
        // ✅ CRITICAL FIX: Clean up progress state when message is completed
        if (messageQueryId) {
          console.log(`🧹 [POLLING] Message completed, cleaning up progress for queryId: ${messageQueryId}`);
          dispatch(completeProgress({ queryId: messageQueryId }));
        }

        return { shouldContinue: false, reason: 'completed', status };
      }

      // ✅ NEW: Update progress from database backup (for polling fallback)
      if (messageQueryId && status === 'active') {
        const messageData = latestMessage as any;

        // Update progress bar from lastProgressUpdate
        if (messageData.lastProgressUpdate) {
          const { stage, progress, details } = messageData.lastProgressUpdate;
          console.log(`🔄 [POLLING] Restoring progress from DB: ${progress}%, stage: ${stage}`);

          dispatch(updateProgress({
            queryId: messageQueryId,
            overallProgress: progress,
            currentStage: stage,
            stageDetails: details,
            status: 'active',
            chatId,
            caseId: latestMessage.caseId,
            advancesProgress: true
          }));
        }

        // Update feed from recentFeedUpdates array (dispatches all items with their IDs for deduplication)
        if (messageData.recentFeedUpdates && messageData.recentFeedUpdates.length > 0) {
          console.log(`📢 [POLLING] Restoring ${messageData.recentFeedUpdates.length} feed updates from DB array`);

          // Dispatch each feed update individually with its DB ID (Redux will handle deduplication)
          messageData.recentFeedUpdates.forEach((feedItem: any, index: number) => {
            const { id: feedId, message: feedMessage, timestamp: feedTimestamp } = feedItem;
            console.log(`📢 [POLLING] Feed ${index + 1}/${messageData.recentFeedUpdates.length}: ${feedMessage.substring(0, 60)}... (ID: ${feedId})`);

            dispatch(updateProgress({
              queryId: messageQueryId,
              overallProgress: messageData.lastProgressUpdate?.progress || 0,
              currentStage: messageData.lastProgressUpdate?.stage || 'initialization',
              stageDetails: feedMessage,
              status: 'active',
              chatId,
              caseId: latestMessage.caseId,
              advancesProgress: false, // Feed update
              feedId, // Pass DB ID for deduplication
              feedTimestamp: feedTimestamp ? new Date(feedTimestamp).getTime() : Date.now()
            }));
          });
        } else if (messageData.lastFeedUpdate) {
          // Fallback to lastFeedUpdate if recentFeedUpdates is not available (backward compatibility)
          const { message: feedMessage } = messageData.lastFeedUpdate;
          console.log(`📢 [POLLING] Restoring feed update from lastFeedUpdate (fallback): ${feedMessage.substring(0, 60)}...`);

          dispatch(updateProgress({
            queryId: messageQueryId,
            overallProgress: messageData.lastProgressUpdate?.progress || 0,
            currentStage: messageData.lastProgressUpdate?.stage || 'initialization',
            stageDetails: feedMessage,
            status: 'active',
            chatId,
            caseId: latestMessage.caseId,
            advancesProgress: false // Feed update
          }));
        }
      }

      // Check for timeout (20+ minutes without update)
      const now = Date.now();
      const messageTimestamp = new Date(latestMessage.timestamp || latestMessage.updatedAt || 0).getTime();
      const timeSinceUpdate = now - messageTimestamp;
      const TIMEOUT_THRESHOLD = 20 * 60 * 1000; // 20 minutes

      if (timeSinceUpdate > TIMEOUT_THRESHOLD) {
        console.error(`[Polling] Message timeout detected for chatId: ${chatId}, patching with error`);

        // ✅ CRITICAL FIX: Clean up progress state on timeout
        if (queryId) {
          console.log(`🧹 [POLLING] Message timeout, cleaning up progress for queryId: ${queryId}`);
          dispatch(completeProgress({ queryId }));
        }

        // Patch message with error status and content
        await dispatch(DBMessageThunks.patch({
          entity: {
            _id: latestMessage._id!,
            ...(latestMessage.metadata && {
              metadata: {
                ...latestMessage.metadata,
                status: 'error'
              }
            }),
            content: `${latestMessage.content}\n\n[ERROR: Query processing timed out after 20 minutes]`
          },
          options: {}
        }));

        return { shouldContinue: false, reason: 'timeout' };
      }

      // Message is still active, continue polling
      return { shouldContinue: true, reason: 'active' };

    } catch (error) {
      console.error('[Polling] Error checking message status:', error);
      return { shouldContinue: false, reason: 'error', error };
    }
  }
);
